// Scrollable message list. Empty state mirrors Reputifly AI Local
// (App.css:329-359) — title centered, composer detached from bottom,
// 720px column. data-state="empty" on body drives the centering.

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "react-feather";
import type { UIMessage } from "../lib/types";
import UserBubble from "./UserBubble";
import AssistantBubble from "./AssistantBubble";
import PendingQueue from "./PendingQueue";
import RequestPublicUpdates from "./RequestPublicUpdates";
import TranscriptSheet from "./TranscriptSheet";
import { useTranscriptSheet } from "../lib/transcriptSheet";
import { useSession } from "../store/session";
import { messageTurnIsActive } from "../lib/toolLiveness";
import { useIsPhone } from "../lib/usePhoneLayout";
import { markScrolling } from "../lib/scrollIdle";

interface Props {
  messages: UIMessage[];
  followToken?: string | null;
}

const NEAR_BOTTOM_PX = 100;

// Durable automatic request rows anchor updates, but are not messages the
// operator typed. Keep their identity/history while presenting system activity.
export function automatedRequestLabel(message: Pick<UIMessage, "id" | "role" | "content">): string | null {
  if (message.role !== "user" || !/^auto-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(message.id)) return null;
  const kind = message.content.match(/^⚙️ (Auto Revision|Approved Revision|Feature|Problem)\n\n/)?.[1];
  return kind ? ({ "Auto Revision": "Automatic revision", "Approved Revision": "Approved revision", Feature: "Approved feature", Problem: "Approved fix" }[kind] ?? null) : null;
}

/** Live "Compacting context…" line with elapsed seconds (server startedAt). */
function CompactingLine({ startedAt }: { startedAt?: string }) {
  const started = startedAt ? Date.parse(startedAt) : NaN;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  const seconds = Number.isFinite(started) ? Math.max(0, Math.floor((now - started) / 1000)) : null;
  return (
    <div className="msg msg--automation msg--compacting" role="status" aria-live="polite">
      <span className="msg__compactSpinner" aria-hidden="true" />
      Compacting context…{seconds !== null ? ` ${seconds} s` : ""}
    </div>
  );
}

/** How long iOS momentum can keep a flick moving after the finger lifts. */
const MOMENTUM_MS = 2500;

export default function MessageList({ messages, followToken }: Props) {
  const queue = useSession(state => state.activeCompanyId ? state.queueByCompany[state.activeCompanyId] : undefined);
  const status = useSession(state => state.activeCompanyId ? state.sessionStatusByCompany[state.activeCompanyId] : undefined);
  const streamingId = useSession(state => state.streamingMessageId);
  const pendingSend = useSession(state => state.activeCompanyId ? state.pendingByCompany?.[state.activeCompanyId] : false);
  const failedUsers = useSession(state => state.failedUserMsgIds);
  const compaction = useSession(state => state.activeCompanyId ? state.compactionByCompany?.[state.activeCompanyId] : undefined);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const userScrolledRef = useRef(false);
  // Layout-clamp scroll events after collapse must not cancel explicit reading.
  const readingTranscriptRef = useRef(false);
  const lastFollowTokenRef = useRef(followToken);
  const [showJump, setShowJump] = useState(false);
  // The transcript pop-up belongs to this chat: close it when the chat changes.
  const transcriptTarget = useTranscriptSheet((state) => state.target);
  useEffect(() => () => useTranscriptSheet.getState().close(), []);
  // Phones (29 Sep): iOS WebKit has no scroll anchoring, so late-loading
  // update cards above the reader pushed the text around. The list keeps its
  // own anchor — the first visible block and its offset — and restores it
  // whenever the content changes size while the reader is not following.
  const isPhone = useIsPhone();
  const phoneRef = useRef(isPhone);
  useEffect(() => { phoneRef.current = isPhone; }, [isPhone]);
  const anchorRef = useRef<{ node: HTMLElement; y: number; range?: Range } | null>(null);
  // A card Julian just opened is the thing held still while it grows (30 Sep 2026): its
  // heading stays put and it opens downward, instead of the reading-line anchor pulling the
  // page every frame against the card's own animation (that fight was the "laggy" open).
  const pinnedUntilRef = useRef(0);
  /** scrollTop right after the list's own anchor correction (to tell it from anyone else's scroll). */
  const ownScrollTopRef = useRef<number | null>(null);
  const inputRef = useRef({ active: false, last: 0 });
  // Positions are measured against the scroll box with getBoundingClientRect, never
  // offsetTop: offsetTop is relative to the nearest positioned ancestor, so a card that
  // loads above the text INSIDE the same message block was invisible to the anchor.
  const topIn = (node: HTMLElement, el: HTMLElement) => node.getBoundingClientRect().top - el.getBoundingClientRect().top;
  const recordAnchor = () => {
    const el = scrollRef.current, content = contentRef.current;
    if (!el || !content) return;
    if (Date.now() < pinnedUntilRef.current) return;
    // Anchor on the block that spans the middle of what you can see (your reading line):
    // anything that loads above it is compensated exactly, so the words do not move.
    const style = getComputedStyle(el);
    const visTop = parseFloat(style.paddingTop) || 0;
    const visBottom = el.clientHeight - (parseFloat(style.paddingBottom) || 0);
    const line = visTop + Math.max(0, visBottom - visTop) / 2;
    // Preserve a visible selection too: adding confirmed instructions must not
    // move the words being copied, even inside a long assistant response.
    const selection = typeof window === "undefined" ? null : window.getSelection?.();
    if (selection && !selection.isCollapsed && selection.rangeCount) {
      const range = selection.getRangeAt(0).cloneRange();
      const container = range.commonAncestorContainer;
      const node = container.nodeType === 1 ? container as HTMLElement : container.parentElement;
      const rect = range.getBoundingClientRect();
      if (node && content.contains(node) && rect.bottom > el.getBoundingClientRect().top && rect.top < el.getBoundingClientRect().bottom) {
        userScrolledRef.current = true;
        readingTranscriptRef.current = true;
        anchorRef.current = { node, range, y: rect.top - el.getBoundingClientRect().top }; return;
      }
    }
    let node: HTMLElement | null = null;
    // Paragraph-level anchors remain stable when a request gains an input group.
    const candidates = content.querySelectorAll?.<HTMLElement>(".msg__text .md > p, .msg__text .md > ul, .msg__text .md > ol, .msg__text .md > pre, .msg--user .msg__text, .requestPublicUpdates, .msg--automation");
    for (const child of Array.from(candidates?.length ? candidates : content.children) as HTMLElement[]) {
      if (!child.offsetHeight) continue;
      if (topIn(child, el) <= line) node = child; else break;
    }
    // Measure the text itself, not the block: a block can gain a card above its text.
    const target = node ? (node.querySelector<HTMLElement>(".msg__text") ?? node) : null;
    anchorRef.current = target ? { node: target, y: topIn(target, el) } : null;
  };

  // History that loads with the chat appears in place (no rise-in); only
  // messages arriving afterwards animate. Phone CSS reads data-hydrating.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.dataset.hydrating = "1";
    const timer = window.setTimeout(() => { delete el.dataset.hydrating; }, 2500);
    return () => window.clearTimeout(timer);
  }, []);

  // ChatPane keys this list by the actual chat ID, so a new chat starts at
  // the bottom without inheriting another chat's scroll position. Apply before
  // paint (not in a later animation frame) to avoid flashing old history.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (followToken && followToken !== lastFollowTokenRef.current) {
      // A newly admitted turn follows its response. Completion (token -> null)
      // must not pull someone away from older messages they are reading.
      userScrolledRef.current = false;
      readingTranscriptRef.current = false;
      setShowJump(false);
    }
    lastFollowTokenRef.current = followToken;
    if (!userScrolledRef.current && !readingTranscriptRef.current) el.scrollTop = el.scrollHeight;
    else {
      // A move can leave total content height unchanged, so ResizeObserver
      // alone cannot protect the reading line. Restore before this paint.
      const a = anchorRef.current;
      if (a?.node.isConnected) {
        const top = a.range ? a.range.getBoundingClientRect().top - el.getBoundingClientRect().top : topIn(a.node, el);
        el.scrollTop += top - a.y; ownScrollTopRef.current = el.scrollTop;
      }
    }
  }, [messages, followToken]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === "undefined") return;
    // Expanding a transcript is a reading action, not new streamed output.
    // Disable bottom-follow before its layout grows (and before ResizeObserver).
    const readTranscript = (event: Event) => {
      const card = (event as CustomEvent<{ card?: HTMLElement }> | undefined)?.detail?.card;
      if (card && el.contains(card)) {
        anchorRef.current = { node: card, y: topIn(card, el) };
        // Held only while it opens: released when its drawer animation ends (cap 450 ms).
        pinnedUntilRef.current = Date.now() + 450;
        const drawer = card.querySelector(".delegateProgress__drawer");
        let released = false;
        // Then the ordinary reading line takes over again at once (never a stale card anchor).
        const release = (e?: Event) => {
          if (released || (e && e.target !== drawer)) return;
          released = true;
          pinnedUntilRef.current = 0;
          drawer?.removeEventListener("transitionend", release);
          recordAnchor();
        };
        drawer?.addEventListener("transitionend", release);
        window.setTimeout(release, 460);
      }
      userScrolledRef.current = true;
      readingTranscriptRef.current = true;
      setShowJump(true);
    };
    el.addEventListener("transcript-reading", readTranscript);
    // A finger on the chat counts as scrolling even before the first scroll event; after it
    // lifts, iOS momentum keeps scrolling for up to ~2.5 s.
    const touchStart = () => { inputRef.current = { active: true, last: Date.now() }; markScrolling(); };
    const touchMove = () => { inputRef.current.last = Date.now(); markScrolling(); };
    const touchEnd = () => { inputRef.current = { active: false, last: Date.now() }; };
    el.addEventListener("touchstart", touchStart, { passive: true });
    el.addEventListener("touchmove", touchMove, { passive: true });
    el.addEventListener("touchend", touchEnd, { passive: true });
    el.addEventListener("touchcancel", touchEnd, { passive: true });
    el.addEventListener("wheel", touchMove, { passive: true });
    const selectionChanged = () => recordAnchor();
    el.ownerDocument?.addEventListener("selectionchange", selectionChanged);
    // Images/fonts and mobile viewport changes can settle after history paint.
    // Keep the latest message visible only while the reader is following it.
    const observer = new ResizeObserver(() => {
      if (!userScrolledRef.current && !readingTranscriptRef.current) el.scrollTop = el.scrollHeight;
      else {
        // Phones AND desktop: hold the reading line. Desktop Chrome's native anchoring holds
        // the TOP of the view, so a card appearing mid-screen pushed the text below it down.
        const a = anchorRef.current;
        if (a?.node.isConnected) {
          const drift = (a.range ? a.range.getBoundingClientRect().top - el.getBoundingClientRect().top : topIn(a.node, el)) - a.y;
          if (drift) { el.scrollTop += drift; ownScrollTopRef.current = el.scrollTop; }
        }
      }
      recordAnchor();
    });
    observer.observe(content);
    observer.observe(el);
    return () => {
      observer.disconnect();
      el.removeEventListener("transcript-reading", readTranscript);
      el.removeEventListener("touchstart", touchStart);
      el.removeEventListener("touchmove", touchMove);
      el.removeEventListener("touchend", touchEnd);
      el.removeEventListener("touchcancel", touchEnd);
      el.removeEventListener("wheel", touchMove);
      el.ownerDocument?.removeEventListener("selectionchange", selectionChanged);
    };
  }, []);

  function onScroll() {
    const el = scrollRef.current;
    if (!el) return;
    // Only the reader's own movement counts (a finger, or the momentum after it, or a
    // wheel): the list's own scroll-to-latest while a reply streams must never hold cards back.
    if (inputRef.current.active || Date.now() - inputRef.current.last < MOMENTUM_MS) markScrolling();
    // Any scroll the list did not make itself ends a just-opened card's hold at once.
    if (pinnedUntilRef.current && (ownScrollTopRef.current === null || Math.abs(el.scrollTop - ownScrollTopRef.current) > 1))
      pinnedUntilRef.current = 0;
    ownScrollTopRef.current = null;
    const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
    recordAnchor();
    if (phoneRef.current) {
      // Phones: "following" means truly at the bottom; 100 px up is reading and
      // must never be yanked back down when content grows. The arrow keeps 100 px.
      if (dist > 2) userScrolledRef.current = true;
      else if (!readingTranscriptRef.current) userScrolledRef.current = false;
      setShowJump(dist > NEAR_BOTTOM_PX || (readingTranscriptRef.current && dist > 2));
      return;
    }
    if (dist > 2) {
      userScrolledRef.current = true;
      setShowJump(dist > NEAR_BOTTOM_PX);
    } else if (!readingTranscriptRef.current) {
      userScrolledRef.current = false;
      setShowJump(false);
    }
  }

  function jumpToBottom() {
    const el = scrollRef.current;
    if (!el) return;
    userScrolledRef.current = false;
    readingTranscriptRef.current = false;
    setShowJump(false);
    el.scrollTo({ top: el.scrollHeight, behavior: typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  }

  // Identify last-assistant index for action-row gating.
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]?.role === "assistant") {
      lastAssistantIdx = i;
      break;
    }
  }
  // Every turn's closing reply gets Copy + Cost Breakdown, not only the chat's newest one
  // (28 Sep, Julian: older replies "randomly" lost their copy button). A reply closes its
  // turn when the next non-event message is the operator's, or when nothing follows.
  const closesTurn = new Set<number>();
  for (let i = messages.length - 1, seenUser = true; i >= 0; i--) {
    const role = messages[i]?.role;
    if (role === "system") continue;
    if (role === "user") { if (!messages[i]?.meta?.nativeInput) seenUser = true; continue; }
    if (role === "assistant" && seenUser) { closesTurn.add(i); seenUser = false; }
  }

  // One linear ownership pass: the request coordinator precedes its final answer.
  let ownerRequest:string|undefined;
  const turns=new Map<string,{messages:UIMessage[];finalFooterId?:string}>();
  const owners=new Map<string,string>();
  const waiting = new Set((queue ?? []).filter(item => !item.dispatching).flatMap(item => [item.id,item.userMsgId]));
  for(let i=0;i<messages.length;i++){
    const message=messages[i]!;
    if(message.role==="system")continue; // event lines belong to no request
    // Added instructions remain on the original request: no new progress card,
    // active footer, transcript owner or cost-action boundary.
    if(message.role==="user"&&!message.meta?.nativeInput&&!waiting.has(message.id)&&!/^(u-resume-|u-bootsweep-)/.test(message.id)){
      ownerRequest=message.id;turns.set(ownerRequest,{messages:[]});
    }
    if(!ownerRequest || message.role==="user"&&waiting.has(message.id))continue;
    const turn=turns.get(ownerRequest)!;
    turn.messages.push(message);owners.set(message.id,ownerRequest);
    if(message.role==="assistant"&&message.meta&&message.content&&!message.isStreaming)turn.finalFooterId=message.id;
  }
  // The optimistic placeholder may precede server acceptance. Its frame is
  // neutral Preparing, never claimed native work. Queue races/rejections clear it.
  const candidateRequest = streamingId && messages.some(message=>message.id===streamingId && message.isStreaming &&
    (messageTurnIsActive(message,status,streamingId) || pendingSend && !["killing","errored"].includes(status?.state ?? "")))
    ? owners.get(streamingId) : undefined;
  const currentRequest = candidateRequest && !failedUsers?.[candidateRequest] ? candidateRequest : undefined;
  const transcriptId = transcriptTarget === "latest" ? [...turns.keys()].at(-1) : transcriptTarget ?? undefined;
  // Projection only: transport/history remain chronological. Exact absorbed
  // receipts join their original prompt; clicking Send now is not acceptance.
  const confirmedByRequest = new Map<string, UIMessage[]>();
  const groupedInputs = new Set<string>();
  for (const message of messages) {
    const input = message.meta?.nativeInput;
    if (message.role !== "user" || input?.state !== "absorbed") continue;
    const owner = input.requestId ? (turns.has(input.requestId) ? input.requestId : undefined) : owners.get(message.id);
    if (!owner) continue; // Do not invent a parent for truncated/orphan history.
    const group = confirmedByRequest.get(owner) ?? [];
    group.push(message); confirmedByRequest.set(owner, group); groupedInputs.add(message.id);
  }
  for (const group of confirmedByRequest.values())
    group.sort((a, b) => (Date.parse(a.createdAt) || 0) - (Date.parse(b.createdAt) || 0));

  return (
    <div className="chat__wrap">
      <div className="chat__scroll" ref={scrollRef} onScroll={onScroll}
        onWheelCapture={() => { readingTranscriptRef.current = false; }}
        onTouchMoveCapture={() => { readingTranscriptRef.current = false; }}
        onKeyDownCapture={event => {
          if (["ArrowUp","ArrowDown","PageUp","PageDown","Home","End"," "].includes(event.key))
            readingTranscriptRef.current = false;
        }}>
        <div ref={contentRef} style={{ width: "100%", minWidth: 0 }}>
        {messages.length === 0 ? (
          <div className="chat__center">
            <h1 className="chat__centerTitle">
              What would you like to{" "}
              <span className="text-gradient">know today</span>?
            </h1>
          </div>
        ) : (
          messages.map((m, i) => groupedInputs.has(m.id) ? null : <Fragment key={m.id}>
            {m.role === "system" ? (
              <div className="msg msg--automation msg--system" role="note" aria-label="Chat event">
                <span className="msg__automationMark" aria-hidden="true" />
                {m.content}
              </div>
            ) : m.role === "user" ? (
              automatedRequestLabel(m) ? <div className="msg msg--automation" role="note" aria-label="System activity">
                <span className="msg__automationMark" aria-hidden="true" />
                {automatedRequestLabel(m)}
              </div> : <UserBubble key={m.id} message={m} />
            ) : (
              <AssistantBubble
                key={m.id}
                message={m}
                isLastInTurn={i === lastAssistantIdx || closesTurn.has(i)}
                publicUpdatesOwned={owners.has(m.id)}
                publicUpdatesRequestId={owners.get(m.id)}
                publicUpdatesActive={owners.get(m.id)===currentRequest && !!currentRequest}
                showResponseMeta={!owners.has(m.id)||turns.get(owners.get(m.id)!)?.finalFooterId===m.id}
              />
            )}
            {confirmedByRequest.has(m.id) && <div className="confirmedInputs" data-request-inputs={m.id} aria-label="Instructions added during this task">
              {confirmedByRequest.get(m.id)!.map(input => <UserBubble key={input.id} message={input} />)}
            </div>}
            {m.role === "user" && turns.has(m.id) &&
              <RequestPublicUpdates requestMessageId={m.id} messages={turns.get(m.id)!.messages} pending={m.id===currentRequest} />}
          </Fragment>)
        )}
        {compaction?.state === "compacting" && <CompactingLine startedAt={compaction.startedAt} />}
        <PendingQueue />
        </div>
      </div>
      {showJump && (
        <button
          type="button"
          className="chat__jumpBtn"
          onClick={jumpToBottom}
          aria-label="Jump to latest"
        >
          <ChevronDown width={18} height={18} />
        </button>
      )}
      {transcriptId && turns.has(transcriptId) && (
        <TranscriptSheet key={transcriptId} requestMessageId={transcriptId} messages={turns.get(transcriptId)!.messages}
          pending={transcriptId === currentRequest}
          live={transcriptId === currentRequest || (transcriptTarget === "latest" && (status?.busy === true || !!status?.autoTurn))} />
      )}
    </div>
  );
}
